import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Plus, Edit2, Trash2, Play, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const RULE_TYPES = ["required", "email", "url", "numeric", "integer", "min_value", "max_value", "min_length", "max_length", "regex", "in_list", "not_in_list"];
const ACTIONS = [{ value: "warn", label: "Warn (allow execution)" }, { value: "block", label: "Block (prevent execution)" }];

function ValidationModal({ ruleset, uploads, onSave, onClose }) {
  const [form, setForm] = useState({ name: ruleset?.name || "", description: ruleset?.description || "", upload_id: ruleset?.upload_id || "", rules: ruleset?.rules || [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const addRule = () => setForm(f => ({ ...f, rules: [...f.rules, { column: "", rule_type: "required", params: {}, action: "warn", message: "" }] }));
  const updateRule = (i, updates) => setForm(f => { const r = [...f.rules]; r[i] = { ...r[i], ...updates }; return { ...f, rules: r }; });
  const removeRule = (i) => setForm(f => ({ ...f, rules: f.rules.filter((_, j) => j !== i) }));

  const handleSave = async () => {
    if (!form.name) { setError("Name required"); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{ruleset ? "Edit" : "New"} Validation Rule Set</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-gray-500">Name *</Label><Input className="mt-1" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div>
              <Label className="text-xs text-gray-500">Apply to File (blank = all files)</Label>
              <Select value={form.upload_id || "__none__"} onValueChange={v => setForm(f => ({ ...f, upload_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="All files" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">All files</SelectItem>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2"><Label className="text-xs text-gray-500">Rules</Label><Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={addRule}><Plus size={10} className="mr-1" /> Add Rule</Button></div>
            {form.rules.map((rule, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 mb-2 p-2 bg-gray-50 rounded border border-gray-100">
                <Input className="h-7 text-xs" value={rule.column} onChange={e => updateRule(i, { column: e.target.value })} placeholder="column" />
                <Select value={rule.rule_type} onValueChange={v => updateRule(i, { rule_type: v })}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{RULE_TYPES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={rule.action} onValueChange={v => updateRule(i, { action: v })}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex gap-1">
                  <Input className="h-7 text-xs flex-1" value={rule.message} onChange={e => updateRule(i, { message: e.target.value })} placeholder="Custom message" />
                  <button onClick={() => removeRule(i)} className="text-red-400 hover:text-red-600 px-1"><XCircle size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Rules"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ValidationRulesPage() {
  const [ruleSets, setRuleSets] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRS, setEditingRS] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState("");

  useEffect(() => {
    load();
    client.get("/files/").then(r => { setUploads(r.data); if (r.data.length > 0) setSelectedUpload(r.data[0]._id); });
  }, []);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/validation/").catch(() => ({ data: [] }));
    setRuleSets(res.data);
    setLoading(false);
  };

  const save = async (form) => {
    if (editingRS) await client.put(`/validation/${editingRS._id}`, form);
    else await client.post("/validation/", form);
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete rule set?")) return;
    await client.delete(`/validation/${id}`);
    load();
  };

  const check = async () => {
    if (!selectedUpload) return;
    setChecking(true); setCheckResult(null);
    try {
      const res = await client.post("/validation/check", { upload_id: selectedUpload });
      setCheckResult(res.data);
    } finally { setChecking(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><ShieldCheck size={18} className="text-cyan-600" /><h1 className="text-2xl font-bold text-gray-900">Validation Rules</h1></div>
          <p className="text-gray-500 text-sm">Define data quality rules that warn or block execution before any API call</p>
        </div>
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingRS(null); setShowModal(true); }}><Plus size={15} className="mr-2" /> New Rule Set</Button>
      </div>
      {/* Check panel */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
        <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-3">Validate a File</p>
        <div className="flex gap-3">
          <Select value={selectedUpload} onValueChange={setSelectedUpload}>
            <SelectTrigger className="flex-1"><SelectValue placeholder="Select file..." /></SelectTrigger>
            <SelectContent>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
          </Select>
          <Button className="bg-[#006080] text-white" onClick={check} disabled={checking}><Play size={14} className="mr-1" />{checking ? "Checking..." : "Check Now"}</Button>
        </div>
        {checkResult && (
          <div className={`mt-3 p-3 rounded-md border ${checkResult.blocked ? "bg-red-50 border-red-200" : checkResult.issue_count > 0 ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
            <p className={`text-sm font-semibold ${checkResult.blocked ? "text-red-700" : checkResult.issue_count > 0 ? "text-amber-700" : "text-green-700"}`}>{checkResult.message}</p>
            {checkResult.issues?.slice(0, 10).map((issue, i) => (
              <div key={i} className="text-xs mt-1 text-gray-600 flex items-center gap-2">
                {issue.action === "block" ? <XCircle size={11} className="text-red-500 flex-shrink-0" /> : <AlertTriangle size={11} className="text-amber-500 flex-shrink-0" />}
                Row {(issue.row_index || 0) + 1} — <strong>{issue.column}</strong>: {issue.message}
              </div>
            ))}
          </div>
        )}
      </div>
      {loading ? <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-14 bg-white border border-gray-200 rounded animate-pulse" />)}</div>
      : ruleSets.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <ShieldCheck size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No validation rules yet</p>
          <Button className="mt-4 bg-[#006080] text-white" onClick={() => setShowModal(true)}>Create first rule set</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {ruleSets.map(rs => (
            <div key={rs._id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
              <ShieldCheck size={18} className="text-cyan-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{rs.name}</p>
                <div className="flex gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">{rs.rules?.length} rules</Badge>
                  {rs.rules?.filter(r => r.action === "block").length > 0 && <Badge variant="secondary" className="text-xs text-red-600">has blocking rules</Badge>}
                </div>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setEditingRS(rs); setShowModal(true); }}><Edit2 size={11} /></Button>
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => del(rs._id)}><Trash2 size={11} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showModal && <ValidationModal ruleset={editingRS} uploads={uploads} onSave={save} onClose={() => { setShowModal(false); setEditingRS(null); }} />}
    </div>
  );
}
